import { DocumentReference, runTransaction, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { MindMapNode, YouTubeVideoEntry } from '../types';
import { videoPatchIntent } from './youtubeService';

/**
 * updateDoc for a note document, safe for video patches.
 *
 * A patch made by buildVideoAppendPatch / buildVideoUpdatePatch is not written as built: its
 * youtubeVideos array comes from the caller's copy of the note, which may predate another upload.
 * Instead the same change (add this video / change these fields of that video) is applied to the
 * note as it is on the server, inside a transaction. Any other patch is written unchanged.
 */
/** Firestore rejects undefined anywhere in the data: drop undefined keys and array items, recursing
 *  only into plain objects and arrays so sentinels like deleteField() pass through untouched. */
const clean = <T,>(v: T): T => {
    if (Array.isArray(v)) return v.filter(x => x !== undefined).map(clean) as unknown as T;
    if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
        const out: Record<string, unknown> = {};
        for (const [k, x] of Object.entries(v)) if (x !== undefined) out[k] = clean(x);
        return out as T;
    }
    return v;
};

export async function safeUpdateDoc(ref: DocumentReference, patch: Record<string, unknown>): Promise<void> {
    const raw = videoPatchIntent.get(patch);
    if (!raw) { await updateDoc(ref, patch as any); return; }
    const intent = raw.kind === 'append' ? { ...raw, entry: clean(raw.entry) } : { ...raw, fields: clean(raw.fields) };
    await runTransaction(db, async tx => {
        const snap = await tx.get(ref);
        if (!snap.exists()) { tx.update(ref, patch as any); return; } // surfaces the usual "no document" error
        const fresh = snap.data() as MindMapNode;
        const others: YouTubeVideoEntry[] = fresh.youtubeVideos || [];
        if (intent.kind === 'append') {
            const id = intent.entry.youtubeVideoId;
            if (fresh.youtubeVideoId === id || others.some(v => v.youtubeVideoId === id)) return; // already linked
            if (!fresh.youtubeVideoId) { const { id: _drop, ...flat } = intent.entry; tx.update(ref, flat as any); return; }
            tx.update(ref, { youtubeVideos: [...others, intent.entry] });
            return;
        }
        if (fresh.youtubeVideoId === intent.videoId) { tx.update(ref, intent.fields as any); return; }
        if (!others.some(v => v.youtubeVideoId === intent.videoId)) return; // the video is no longer on this note
        tx.update(ref, { youtubeVideos: others.map(v => (v.youtubeVideoId === intent.videoId ? { ...v, ...intent.fields } : v)) });
    });
}
